import React, { type ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';

interface FormattedMessageProps {
  content: string;
  className?: string;
}

interface ParsedContent {
  type: 'text' | 'bold' | 'italic' | 'code' | 'list' | 'codeBlock' | 'heading' | 'lineBreak';
  content: string;
  language?: string;
}

const parseMarkdown = (text: string): (ParsedContent | ReactNode)[] => {
  // Filter out all asterisks from the text
  const cleanText = text.replace(/\*/g, '');
  
  const parts: (ParsedContent | ReactNode)[] = [];
  let currentIndex = 0;

  // Code block pattern (```language\ncode\n```)
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  let codeBlockMatch;
  const codeBlockMatches: { start: number; end: number; language: string; content: string }[] = [];

  while ((codeBlockMatch = codeBlockRegex.exec(cleanText)) !== null) {
    codeBlockMatches.push({
      start: codeBlockMatch.index,
      end: codeBlockMatch.index + codeBlockMatch[0].length,
      language: codeBlockMatch[1] || 'text',
      content: codeBlockMatch[2].trim(),
    });
  }

  // Process code blocks first
  if (codeBlockMatches.length > 0) {
    codeBlockMatches.forEach((match, idx) => {
      if (currentIndex < match.start) {
        parts.push(...parseMarkdown(cleanText.slice(currentIndex, match.start)));
      }
      parts.push({
        type: 'codeBlock',
        content: match.content,
        language: match.language,
      });
      currentIndex = match.end;
    });

    if (currentIndex < cleanText.length) {
      parts.push(...parseMarkdown(cleanText.slice(currentIndex)));
    }

    return parts;
  }

  // If no code blocks, continue with other patterns
  const lines = cleanText.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Empty line
    if (line.trim() === '') {
      if (i < lines.length - 1) {
        parts.push({ type: 'lineBreak', content: '' });
      }
      continue;
    }

    // Heading pattern (# Heading, ## Heading, etc.)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      parts.push({
        type: 'heading',
        content: headingMatch[2],
      });
      continue;
    }

    // List pattern (- item, • item, but NOT * for italics/bold)
    // Changed to only match - and • at start of line, not *
    const listMatch = line.match(/^[\s]*([-•])\s+(.+)$/);
    if (listMatch) {
      parts.push({
        type: 'list',
        content: listMatch[2],
      });
      continue;
    }

    // Regular line with bold, italic, and inline code
    const lineContent = parseInlineMarkdown(line);
    parts.push(...lineContent);

    if (i < lines.length - 1) {
      parts.push({ type: 'lineBreak', content: '' });
    }
  }

  return parts;
};

const parseInlineMarkdown = (line: string): (ParsedContent | ReactNode)[] => {
  const parts: (ParsedContent | ReactNode)[] = [];
  let currentIndex = 0;

  // Find all patterns with proper non-greedy matching
  // No asterisk patterns since we filter them out at the start
  const patterns = [
    { regex: /__([^_]+?)__/g, type: 'bold' },          // __bold__
    { regex: /_([^_]{1,}?)_/g, type: 'italic' },       // _italic_
    { regex: /`([^`]+?)`/g, type: 'code' },            // `code`
  ];

  const matches: { index: number; endIndex: number; type: string; content: string }[] = [];

  patterns.forEach(({ regex, type }) => {
    let match;
    // Reset regex lastIndex before use
    regex.lastIndex = 0;
    while ((match = regex.exec(line)) !== null) {
      matches.push({
        index: match.index,
        endIndex: match.index + match[0].length,
        type,
        content: match[1],
      });
    }
  });

  // Sort matches by index
  matches.sort((a, b) => a.index - b.index);

  // Remove overlapping matches (keep first match)
  const filteredMatches: { index: number; endIndex: number; type: string; content: string }[] = [];
  for (const match of matches) {
    const overlaps = filteredMatches.some(
      (m) => (match.index >= m.index && match.index < m.endIndex) ||
             (match.endIndex > m.index && match.endIndex <= m.endIndex)
    );
    if (!overlaps) {
      filteredMatches.push(match);
    }
  }

  // Build parts
  filteredMatches.forEach((match) => {
    if (currentIndex < match.index) {
      parts.push({
        type: 'text',
        content: line.slice(currentIndex, match.index),
      });
    }

    parts.push({
      type: match.type as 'bold' | 'italic' | 'code',
      content: match.content,
    });

    currentIndex = match.endIndex;
  });

  if (currentIndex < line.length) {
    parts.push({
      type: 'text',
      content: line.slice(currentIndex),
    });
  }

  return parts.length === 0 ? [{ type: 'text', content: line }] : parts;
};

const CodeBlock = ({ content, language }: { content: string; language: string }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-3 sm:my-4 rounded-lg sm:rounded-xl overflow-hidden border border-white/10 bg-black/40 backdrop-blur-sm max-w-full">
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 bg-white/5 border-b border-white/5">
        <span className="text-[10px] sm:text-xs font-mono text-gray-400 uppercase tracking-widest truncate">
          {language || 'code'}
        </span>
        <button
          onClick={copyToClipboard}
          className="p-1 sm:p-1.5 rounded-lg hover:bg-white/10 transition-colors text-gray-500 hover:text-white flex-shrink-0 ml-2"
          title="Copy code"
        >
          {copied ? (
            <>
              <Check size={14} className="sm:hidden" />
              <Check size={16} className="hidden sm:block" />
            </>
          ) : (
            <>
              <Copy size={14} className="sm:hidden" />
              <Copy size={16} className="hidden sm:block" />
            </>
          )}
        </button>
      </div>
      <pre className="p-3 sm:p-4 overflow-x-auto text-[11px] sm:text-xs md:text-sm text-gray-200 font-mono leading-relaxed max-w-full whitespace-pre-wrap break-words">
        <code>{content}</code>
      </pre>
    </div>
  );
};

export const FormattedMessage: React.FC<FormattedMessageProps> = ({ content, className = '' }) => {
  const parsed = parseMarkdown(content);

  return (
    <div className={`prose prose-invert max-w-none ${className}`}>
      {parsed.map((part, idx) => {
        if (part && typeof part === 'object' && 'type' in part) {
          const p = part as ParsedContent;

          switch (p.type) {
            case 'text':
              return <span key={idx}>{p.content}</span>;

            case 'bold':
              return (
                <strong key={idx} className="font-extrabold text-white drop-shadow-lg">
                  {p.content}
                </strong>
              );

            case 'italic':
              return (
                <em key={idx} className="italic text-gray-300">
                  {p.content}
                </em>
              );

            case 'code':
              return (
                <code
                  key={idx}
                  className="px-2 py-0.5 bg-black/30 border border-white/10 rounded text-xs font-mono text-amber-300"
                >
                  {p.content}
                </code>
              );

            case 'heading':
              return (
                <h3 key={idx} className="text-lg font-bold text-white mt-4 mb-2 bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-rose-400">
                  {p.content}
                </h3>
              );

            case 'list':
              return (
                <div key={idx} className="flex items-start gap-3 my-2 ml-2">
                  <span className="text-blue-400 font-bold mt-0.5">•</span>
                  <span className="text-gray-200">{p.content}</span>
                </div>
              );

            case 'codeBlock':
              return <CodeBlock key={idx} content={p.content} language={p.language || ''} />;

            case 'lineBreak':
              return <div key={idx} className="h-1" />;

            default:
              return null;
          }
        }
        return part;
      })}
    </div>
  );
};

export default FormattedMessage;
